import _typeof from '@babel/runtime/helpers/typeof';
import { getEpoch } from '../utility.js';

/*  Utility function
 *  @function sortData
 *  @param {array of rowObjects} rowData
 *  @param {string} sortField
 *  @param {'asc' | 'dsc' | null} sortDirection
 *  @param {'numeric' | 'boolean' | 'date' | 'character' | null} sortType
 *  @returns {array of rowObjects} - values are now sorted
 *
 *  NOTE: Special data types like boolean  MUST have cellObjects like {cellDisplay, cellValue}
 *  There's not a good way to tell the dom what to display for these types without it
 */
var sortData = function sortData(rowData, sortField, sortDirection, sortType) {
  return rowData.sort(function (rowA, rowB) {
    var _rowA$sortField, _rowB$sortField;
    // use cellValue if set
    // if not: the cellValue might be null/undefined which is why the ternary is needed to set field value
    var valueOfA = ((_rowA$sortField = rowA[sortField]) === null || _rowA$sortField === void 0 ? void 0 : _rowA$sortField.cellValue) || (_typeof(rowA[sortField]) === 'object' ? null : rowA[sortField]);
    var valueOfB = ((_rowB$sortField = rowB[sortField]) === null || _rowB$sortField === void 0 ? void 0 : _rowB$sortField.cellValue) || (_typeof(rowB[sortField]) === 'object' ? null : rowB[sortField]);
    if (sortType === 'boolean') {
      var _rowA$sortField2, _rowB$sortField2, _rowA$sortField3, _rowB$sortField3;
      //does not consider values without the cell object because these will not display properly
      var cellA = (_rowA$sortField2 = rowA[sortField]) === null || _rowA$sortField2 === void 0 ? void 0 : _rowA$sortField2.cellValue;
      var cellB = (_rowB$sortField2 = rowB[sortField]) === null || _rowB$sortField2 === void 0 ? void 0 : _rowB$sortField2.cellValue;
      if ((typeof cellA !== 'boolean' && cellA != null) | (typeof cellB !== 'boolean' && cellB != null)) {
        console.error('Hey Canvas User, Looks like you selected boolean sort but some cellValue is not a boolean (or null) - this sort will produce weird results');
      }

      //using double equal compare to check for null OR undefined values
      var boolValueA = cellA == null ? -1 : Boolean((_rowA$sortField3 = rowA[sortField]) === null || _rowA$sortField3 === void 0 ? void 0 : _rowA$sortField3.cellValue);
      var boolValueB = cellB == null ? -1 : Boolean((_rowB$sortField3 = rowB[sortField]) === null || _rowB$sortField3 === void 0 ? void 0 : _rowB$sortField3.cellValue);

      //dsc: null/undefined - false - true
      if (sortDirection === 'dsc') {
        return boolValueA - boolValueB;
      }

      //asc: true - false - null/undefined
      if (sortDirection === 'asc') {
        return boolValueB - boolValueA;
      }
    } else if (sortType === 'date') {
      var _rowA$sortField4, _rowA$sortField5, _rowB$sortField4, _rowB$sortField5;
      //if cellValue is date object: valueOfA -null
      //if cellValue is date string:  valueOfA - date string
      //if field is date object: valueOfA= null (date is object type)
      //if field is date string: valueOfA -null (gets read as object too!)
      var valueForDateA = valueOfA || (((_rowA$sortField4 = rowA[sortField]) === null || _rowA$sortField4 === void 0 ? void 0 : _rowA$sortField4.cellValue) instanceof Date ? (_rowA$sortField5 = rowA[sortField]) === null || _rowA$sortField5 === void 0 ? void 0 : _rowA$sortField5.cellValue : rowA[sortField] || null);
      var valueForDateB = valueOfB || (((_rowB$sortField4 = rowB[sortField]) === null || _rowB$sortField4 === void 0 ? void 0 : _rowB$sortField4.cellValue) instanceof Date ? (_rowB$sortField5 = rowB[sortField]) === null || _rowB$sortField5 === void 0 ? void 0 : _rowB$sortField5.cellValue : rowB[sortField] || null);

      //handle undefined/null values by setting them to lowest sort value
      var dateA = getEpoch(valueForDateA) || Number.MIN_SAFE_INTEGER;
      var dateB = getEpoch(valueForDateB) || Number.MIN_SAFE_INTEGER;
      //dsc: newest to oldest - null
      if (sortDirection === 'dsc') {
        return dateB - dateA;
      }
      //asc:  null - oldest-newest
      if (sortDirection === 'asc') {
        return dateA - dateB;
      }
    } else if (sortType === 'numeric') {
      // if this comes in as a string, we remove comas and
      // change all values to numbers for the most accurate sorting
      if (typeof valueOfA === 'string') {
        valueOfA = parseFloat(valueOfA.split(',').join(''));
      }
      if (typeof valueOfB === 'string') {
        valueOfB = parseFloat(valueOfB.split(',').join(''));
      }
      //handle undefined/null values by setting them to lowest sort value
      var numberA = valueOfA || Number.MIN_SAFE_INTEGER;
      var numberB = valueOfB || Number.MIN_SAFE_INTEGER;
      //dsc: highest to lowest - null
      if (sortDirection === 'dsc') {
        return numberB - numberA;
      }
      //asc: null - lowest to highest
      if (sortDirection === 'asc') {
        return numberA - numberB;
      }
    } else {
      var _valueOfA, _valueOfB;
      //double equal check to assign undefined OR null values to end of sort
      //convert to string just in case we got a special data type
      //convert to uppercase so case insensitive sort
      var stringA = valueOfA == null ? 'ZZZZZZZZ' : (_valueOfA = valueOfA) === null || _valueOfA === void 0 ? void 0 : _valueOfA.toString().toUpperCase();
      var stringB = valueOfB == null ? 'ZZZZZZZZ' : (_valueOfB = valueOfB) === null || _valueOfB === void 0 ? void 0 : _valueOfB.toString().toUpperCase();
      // dsc: null - reverse alphabetical
      if (sortDirection === 'dsc') {
        return stringA > stringB ? -1 : stringA > stringB ? 1 : 0;
      }
      //asc: alphabetical - null
      if (sortDirection === 'asc') {
        return stringA < stringB ? -1 : stringA > stringB ? 1 : 0;
      }
    }
  });
};

export { sortData };
